import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

export const IsiDTCTheme = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#effaf2',
            100: '#d8f3de',
            200: '#b3e7c1',
            300: '#82d59d',
            400: '#4abb74',
            500: '#0C9748',
            600: '#0a8240',
            700: '#096b35',
            800: '#0b552d',
            900: '#0a4726',
            950: '#042815',
        },
        accent: {
            50: '#fff3f1',
            100: '#ffe3df',
            200: '#ffcdc5',
            300: '#ffab9d',
            400: '#ff7a64',
            500: '#ff593e',
            600: '#ed3315',
            700: '#c8270d',
            800: '#a5240f',
            900: '#882414',
            950: '#4b0e04',
        },
    },
    components: {
        button: {
            root: {
                borderRadius: '4px',
                label: {
                    fontWeight: '400',
                },
            },
            css: () => `
              .p-button {
                font-family: 'Montserrat', sans-serif !important;
                font-size: 14px !important;
                line-height: 20px !important;
                font-weight: 400 !important;
              }
            `,
            colorScheme: {
                light: {
                    outlined: {
                        secondary: {
                            borderColor: '#79747E',
                            color: '#000000',
                            hoverBackground: 'rgba(0, 0, 0, 0.04)',
                            activeBackground: 'rgba(0, 0, 0, 0.08)',
                        },
                    },
                },
                dark: {
                    outlined: {
                        secondary: {
                            borderColor: '#CAC4D0',
                            color: '#FFFFFF',
                            hoverBackground: 'rgba(255, 255, 255, 0.08)',
                            activeBackground: 'rgba(255, 255, 255, 0.12)',
                        },
                    },
                },
            },
        },
        tooltip: {
            root: {
                maxWidth: '320px',
            },
        },
        radiobutton: {
            colorScheme: {
                light: {
                    root: {
                        borderColor: '{surface.400}',
                    },
                },
                dark: {
                    root: {
                        borderColor: '{surface.500}',
                    },
                },
            },
            root: {
                width: '1.25rem',
                height: '1.25rem',
                checkedBorderColor: '{primary.color}',
                checkedBackground: '{primary.color}',
            },
            icon: {
                checkedColor: '#ffffff',
                size: '0.75rem',
            },
            extend: {
                disclosureRefusedState: {
                    root: {
                        checkedBorderColor: '{orange.500}',
                        checkedBackground: '{orange.500}',
                    },
                },
                disclosureAcceptedState: {
                    root: {
                        checkedBorderColor: '{primary.500}',
                        checkedBackground: '{primary.500}',
                    },
                },
            },
            css: ({ dt }) => `
              .p-radiobutton.negative-radio-option.p-radiobutton-checked .p-radiobutton-box {
                  border-color: ${dt('radiobutton.extend.disclosureRefusedState.checkedBorderColor')};
                  background: ${dt('radiobutton.extend.disclosureRefusedState.checkedBackground')};
              }
              .p-radiobutton.negative-radio-option.p-radiobutton-checked .p-radiobutton-input:hover + .p-radiobutton-box {
                  border-color: ${dt('radiobutton.extend.disclosureRefusedState.checkedBorderColor')};
                  background: ${dt('radiobutton.extend.disclosureRefusedState.checkedBackground')};
              }
              .p-radiobutton.positive-radio-option.p-radiobutton-checked .p-radiobutton-box {
                  border-color: ${dt('radiobutton.extend.disclosureAcceptedState.checkedBorderColor')};
                  background: ${dt('radiobutton.extend.disclosureAcceptedState.checkedBackground')};
              }
              .p-radiobutton.positive-radio-option.p-radiobutton-checked .p-radiobutton-input:hover + .p-radiobutton-box {
                  border-color: ${dt('radiobutton.extend.disclosureAcceptedState.checkedBorderColor')};
                  background: ${dt('radiobutton.extend.disclosureAcceptedState.checkedBackground')};
              }

              /* Disabled state - softer icon color */
              .p-radiobutton-checked.p-disabled .p-radiobutton-box .p-radiobutton-icon {
                  background: ${dt('surface.300')};
              }
              .dark .p-radiobutton-checked.p-disabled .p-radiobutton-box .p-radiobutton-icon {
                  background: ${dt('surface.400')};
              }

              /* Positive option disabled */
              .p-radiobutton.positive-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box {
                  border-color: ${dt('primary.300')};
                  background: ${dt('primary.200')};
              }
              .dark .p-radiobutton.positive-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box {
                  border-color: ${dt('primary.600')};
                  background: ${dt('primary.700')};
              }
              .p-radiobutton.positive-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box .p-radiobutton-icon {
                  background: ${dt('primary.500')};
              }
              .dark .p-radiobutton.positive-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box .p-radiobutton-icon {
                  background: ${dt('primary.400')};
              }

              /* Negative option disabled */
              .p-radiobutton.negative-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box {
                  border-color: ${dt('orange.300')};
                  background: ${dt('orange.200')};
              }
              .dark .p-radiobutton.negative-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box {
                  border-color: ${dt('orange.600')};
                  background: ${dt('orange.700')};
              }
              .p-radiobutton.negative-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box .p-radiobutton-icon {
                  background: ${dt('orange.500')};
              }
              .dark .p-radiobutton.negative-radio-option.p-radiobutton-checked.p-disabled .p-radiobutton-box .p-radiobutton-icon {
                  background: ${dt('orange.400')};
              }
            `,
        },
        accordion: {
            colorScheme: {
                light: {
                    header: {
                        background: '{primary.50}',
                        hoverBackground: '{primary.100}',
                        activeBackground: '{primary.100}',
                        activeHoverBackground: '{primary.200}',
                    },
                },
                dark: {
                    header: {
                        background: '{surface.800}',
                        hoverBackground: '{surface.700}',
                        activeBackground: '{surface.700}',
                        activeHoverBackground: '{surface.600}',
                    },
                },
            },
            header: {
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem 0 0.5rem 0',
            },
            content: {
                padding: '0px',
            },
            panel: {
                borderWidth: '0px',
            },
        },
        skeleton: {
            colorScheme: {
                light: {
                    root: {
                        background: '{primary.50}',
                        animationBackground: '{primary.100}',
                    },
                },
                dark: {
                    root: {
                        background: '{primary.50}',
                        animationBackground: '{primary.100}',
                    },
                },
            },
        },
        inputtext: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                        invalidPlaceholderColor: '{orange.600}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                        invalidPlaceholderColor: '{orange.400}',
                    },
                },
            },
            root: {
                borderRadius: '0',
                paddingX: '0',
                background: 'transparent',
                shadow: 'none',
            },
            css: () => `
              .p-inputtext,
              .p-select,
              .p-multiselect,
              .p-cascadeselect,
              .p-treeselect,
              .p-autocomplete .p-autocomplete-input,
              .p-datepicker-input,
              .p-inputnumber-input,
              .p-textarea {
                border-radius: 0 !important;
                border-top: 0 !important;
                border-left: 0 !important;
                border-right: 0 !important;
                border-bottom: 1px solid #000 !important;
                background: transparent !important;
                box-shadow: none !important;
                font-family: 'Montserrat', sans-serif !important;
                font-size: 14px !important;
                line-height: 18px !important;
                font-weight: 400 !important;
                height: 38px !important;
                min-height: 38px !important;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
                box-sizing: border-box !important;
              }
              .p-select,
              .p-multiselect,
              .p-cascadeselect,
              .p-treeselect {
                display: flex !important;
                align-items: center !important;
              }
              .p-select .p-select-label,
              .p-multiselect .p-multiselect-label,
              .p-cascadeselect .p-cascadeselect-label,
              .p-treeselect .p-treeselect-label {
                padding-left: 0 !important;
                padding-right: 0 !important;
                font-family: 'Montserrat', sans-serif !important;
                font-size: 14px !important;
                line-height: 18px !important;
                font-weight: 400 !important;
              }
              .p-inputtext:disabled,
              .p-inputtext.p-disabled,
              .p-select.p-disabled,
              .p-multiselect.p-disabled,
              .p-cascadeselect.p-disabled,
              .p-treeselect.p-disabled,
              .p-textarea:disabled,
              .p-textarea.p-disabled,
              .p-datepicker-input:disabled,
              .p-inputnumber-input:disabled {
                opacity: 0.75 !important;
                cursor: not-allowed !important;
                border-bottom-style: dashed !important;
                border-bottom-color: #9ca3af !important;
                color: #374151 !important;
                background: repeating-linear-gradient(
                  45deg,
                  rgba(0, 0, 0, 0.03),
                  rgba(0, 0, 0, 0.03) 6px,
                  transparent 6px,
                  transparent 12px
                ) !important;
              }
              .p-select.p-disabled .p-select-label,
              .p-multiselect.p-disabled .p-multiselect-label,
              .p-cascadeselect.p-disabled .p-cascadeselect-label,
              .p-treeselect.p-disabled .p-treeselect-label {
                color: #374151 !important;
                opacity: 1 !important;
              }
              .isi-dtc-dark .p-inputtext:disabled,
              .isi-dtc-dark .p-inputtext.p-disabled,
              .isi-dtc-dark .p-select.p-disabled,
              .isi-dtc-dark .p-multiselect.p-disabled,
              .isi-dtc-dark .p-cascadeselect.p-disabled,
              .isi-dtc-dark .p-treeselect.p-disabled,
              .isi-dtc-dark .p-textarea:disabled,
              .isi-dtc-dark .p-textarea.p-disabled,
              .isi-dtc-dark .p-datepicker-input:disabled,
              .isi-dtc-dark .p-inputmask:disabled,
              .isi-dtc-dark .p-inputnumber-input:disabled {
                border-bottom-color: #6b7280 !important;
                color: #e5e7eb !important;
                background: repeating-linear-gradient(
                  45deg,
                  rgba(255, 255, 255, 0.04),
                  rgba(255, 255, 255, 0.04) 6px,
                  transparent 6px,
                  transparent 12px
                ) !important;
              }
              .isi-dtc-dark .p-select.p-disabled .p-select-label,
              .isi-dtc-dark .p-multiselect.p-disabled .p-multiselect-label,
              .isi-dtc-dark .p-cascadeselect.p-disabled .p-cascadeselect-label,
              .isi-dtc-dark .p-treeselect.p-disabled .p-treeselect-label {
                color: #e5e7eb !important;
                opacity: 1 !important;
              }
              .p-inputtext,
              .p-textarea,
              .p-datepicker-input,
              .p-inputnumber-input,
              .p-autocomplete .p-autocomplete-input {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
              .isi-dtc-dark .p-inputtext,
              .isi-dtc-dark .p-select,
              .isi-dtc-dark .p-multiselect,
              .isi-dtc-dark .p-cascadeselect,
              .isi-dtc-dark .p-treeselect,
              .isi-dtc-dark .p-autocomplete .p-autocomplete-input,
              .isi-dtc-dark .p-datepicker-input,
              .isi-dtc-dark .p-inputnumber-input,
              .isi-dtc-dark .p-textarea {
                border-bottom-color: #fff !important;
              }
              .p-inputtext.p-invalid,
              .p-select.p-invalid,
              .p-multiselect.p-invalid,
              .p-cascadeselect.p-invalid,
              .p-treeselect.p-invalid,
              .p-inputnumber-input.p-invalid,
              .p-textarea.p-invalid {
                border-bottom-color: var(--p-orange-500) !important;
              }
              .p-inputtext:enabled:focus,
              .p-inputtext:enabled:hover,
              .p-select:not(.p-disabled):hover,
              .p-select:not(.p-disabled).p-focus,
              .p-textarea:enabled:focus,
              .p-textarea:enabled:hover {
                outline: none !important;
                box-shadow: none !important;
              }
            `,
        },
        select: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                        invalidPlaceholderColor: '{orange.600}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                        invalidPlaceholderColor: '{orange.400}',
                    },
                },
            },
        },
        // datepicker, inputmask, inputnumber use inputtext internally - covered by inputtext override
        textarea: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                        invalidPlaceholderColor: '{orange.600}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                        invalidPlaceholderColor: '{orange.400}',
                    },
                },
            },
        },
        autocomplete: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                        invalidPlaceholderColor: '{orange.600}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                        invalidPlaceholderColor: '{orange.400}',
                    },
                },
            },
        },
        multiselect: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                        invalidPlaceholderColor: '{orange.600}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                        invalidPlaceholderColor: '{orange.400}',
                    },
                },
            },
        },
        cascadeselect: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                        invalidPlaceholderColor: '{orange.600}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                        invalidPlaceholderColor: '{orange.400}',
                    },
                },
            },
        },
        listbox: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                    },
                },
            },
        },
        treeselect: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                        invalidPlaceholderColor: '{orange.600}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                        invalidPlaceholderColor: '{orange.400}',
                    },
                },
            },
        },
        checkbox: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                    },
                },
            },
        },
        toggleswitch: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                    },
                },
            },
        },
        togglebutton: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                    },
                },
            },
        },
        selectbutton: {
            colorScheme: {
                light: {
                    root: {
                        invalidBorderColor: '{orange.400}',
                    },
                },
                dark: {
                    root: {
                        invalidBorderColor: '{orange.300}',
                    },
                },
            },
        },
    },
});
